import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ceil, find, without } from 'lodash-es';
import {
  combineLatest,
  map,
  merge,
  Observable,
  of,
  partition,
  Subject,
  tap,
} from 'rxjs';
import { GridHelpers } from './helpers/grid';
import { AncillaryArgument, QuoteData, QuoteType } from './quote-data';
import { AppState } from './shared-quoting.module';

@Injectable()
export class PlantAncillariesService {
  calculated$: Observable<boolean>;
  private calculated = new Subject<boolean>();

  private settings: Observable<any>;
  private forkLiftId: number;
  private floorHeightForReachTruck: number;
  private reachTruckId: number;
  private floorHeightForScissorLift: number;
  private scissorLiftId: number;
  private longestBeamLengthForTwoTimesPlant: number;
  private longestJoistLengthForTwoTimesPlant: number;
  private mobileTowerId: number;

  constructor(private store: Store<AppState>) {
    this.calculated$ = this.calculated.asObservable();
    this.settings = this.store.select(
      (state) => state.settings.currentSettings
    );
  }

  calculate(quoteData: QuoteData): Observable<QuoteData> {
    const [plant, noplant] = partition(
      of(quoteData),
      (qd: QuoteData) => qd.GeneralData.CostAncillaries
    );
    return merge(
      plant.pipe(
        tap(() => this.calculated.next(false)),
        (val) => combineLatest([val, this.settings]),
        map(([qd, settings]) => {
          this.forkLiftId = settings.get('forkLiftId');
          this.reachTruckId = settings.get('reachTruckId');
          this.scissorLiftId = settings.get('scissorLiftId');
          this.mobileTowerId = settings.get('mobileTowerId');
          this.floorHeightForReachTruck = settings.get(
            'floorHeightForReachTruck'
          );
          this.floorHeightForScissorLift = settings.get(
            'floorHeightForScissorLift'
          );
          this.longestBeamLengthForTwoTimesPlant = settings.get(
            'longestBeamLengthForTwoTimesPlant'
          );
          this.longestJoistLengthForTwoTimesPlant = settings.get(
            'longestJoistLengthForTwoTimesPlant'
          );

          const extraOverJoistsAncillaryId = settings.get(
            'extraOverJoistsAncillaryId'
          );
          const extraOverJoistsAncillaryArgument =
            quoteData.AncillaryData.AncillaryArguments.find(
              (aa) => aa.Ancillary === extraOverJoistsAncillaryId
            );

          if (
            ((qd.GeneralData.QuoteType === QuoteType.Budget &&
              settings.get('extraOverJoistsBudgetQuoteEnabled')) ||
              (qd.GeneralData.QuoteType === QuoteType.Internal &&
                settings.get('extraOverJoistsInternalQuoteEnabled')) ||
              (qd.GeneralData.QuoteType === QuoteType.Distributor &&
                settings.get('extraOverJoistsDistributorQuoteEnabled'))) &&
            !extraOverJoistsAncillaryArgument
          ) {
            quoteData.AncillaryData.AncillaryArguments.push({
              Ancillary: extraOverJoistsAncillaryId,
              Quantity: 1,
              IsExtraOver: true,
              Length: 0,
              Height: 0,
              IsPlant: true,
              AutoCalculateHeight: false,
            });
          } else if (extraOverJoistsAncillaryArgument) {
            extraOverJoistsAncillaryArgument.IsPlant = true;
          }

          this.calculatePlantAncillaries(qd);
          this.calculated.next(true);
          return qd;
        })
      ),
      noplant
    );
  }

  private calculatePlantAncillaries(quoteData: QuoteData): void {
    if (!quoteData.GeneralData.CostAncillaries) {
      return;
    }
    if (!quoteData.AncillaryData.AncillaryArguments) {
      quoteData.AncillaryData.AncillaryArguments = [];
    }
    if (quoteData.AncillaryData.CalcPlant === false) {
      this.removePlantAncillaries(quoteData);
    } else if (quoteData.AncillaryData.CalcPlant) {
      this.removePlantAncillaries(quoteData);
      if (quoteData.GeneralData.CostInstallation) {
        const weeksOfWork = ceil(quoteData.InstallationData.Days / 5);
        const totalFloorHeight =
          GridHelpers.calculateTotalFloorHeight(quoteData);
        quoteData.AncillaryData.AncillaryArguments.push(
          this.calculatePlantItemOne(quoteData, weeksOfWork, totalFloorHeight)
        );
        const plantItemTwo = this.calculatePlantItemTwo(
          quoteData,
          weeksOfWork,
          totalFloorHeight
        );
        if (plantItemTwo.Ancillary !== -1) {
          quoteData.AncillaryData.AncillaryArguments.push(plantItemTwo);
        }
      }
    }
  }

  private calculatePlantItemOne(
    quoteData: QuoteData,
    weeksOfWork: number,
    totalFloorHeight: number
  ): AncillaryArgument {
    const plantItemOne = new AncillaryArgument();
    plantItemOne.Ancillary = this.forkLiftId;
    plantItemOne.Quantity = 1;
    plantItemOne.Length = 0;
    plantItemOne.Height = 0;
    plantItemOne.IsExtraOver = true;
    if (totalFloorHeight >= this.floorHeightForReachTruck) {
      plantItemOne.Ancillary = this.reachTruckId;
    }
    if (this.beamLengthNeedsTwoTimesPlant(quoteData)) {
      plantItemOne.Quantity = 2;
    }
    plantItemOne.Quantity *= weeksOfWork;
    plantItemOne.IsPlant = true;
    return plantItemOne;
  }

  private calculatePlantItemTwo(
    quoteData: QuoteData,
    weeksOfWork: number,
    totalFloorHeight: number
  ): AncillaryArgument {
    const plantItemTwo = new AncillaryArgument();
    plantItemTwo.Ancillary = this.scissorLiftId;
    plantItemTwo.Length = 0;
    plantItemTwo.Height = 0;
    plantItemTwo.Quantity =
      totalFloorHeight >= this.floorHeightForScissorLift ? 2 : 1;
    plantItemTwo.IsExtraOver = true;
    if (
      this.beamLengthNeedsTwoTimesPlant(quoteData) ||
      this.joistLengthNeedsTwoTimesPlant(quoteData)
    ) {
      plantItemTwo.Quantity = 2;
    }
    plantItemTwo.Quantity *= weeksOfWork;
    plantItemTwo.IsPlant = true;
    return plantItemTwo;
  }

  private beamLengthNeedsTwoTimesPlant(quoteData: QuoteData): boolean {
    return (
      quoteData.AncillaryData.LongestBeamLength >=
      this.longestBeamLengthForTwoTimesPlant
    );
  }

  private joistLengthNeedsTwoTimesPlant(quoteData: QuoteData): boolean {
    return (
      quoteData.AncillaryData.LongestJoistLength >=
      this.longestJoistLengthForTwoTimesPlant
    );
  }

  private removePlantAncillaries(quoteData: QuoteData): void {
    const mobileTower = find(quoteData.AncillaryData.AncillaryArguments, {
      Ancillary: this.mobileTowerId,
    });
    const forkLift = find(quoteData.AncillaryData.AncillaryArguments, {
      Ancillary: this.forkLiftId,
    });
    const scissorLift = find(quoteData.AncillaryData.AncillaryArguments, {
      Ancillary: this.scissorLiftId,
    });
    const reachTruck = find(quoteData.AncillaryData.AncillaryArguments, {
      Ancillary: this.reachTruckId,
    });
    quoteData.AncillaryData.AncillaryArguments = without(
      quoteData.AncillaryData.AncillaryArguments,
      mobileTower,
      forkLift,
      scissorLift,
      reachTruck
    );
  }
}
